import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import { Icon } from 'components/common';
import { colours } from 'utils/theme';

const ItemWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  position: relative;
`;

/** Single visual bubble; separate buttons (valid HTML, close inside border). */
const PillShell = styled.div`
  display: inline-flex;
  align-items: stretch;
  max-width: 100%;
  border-radius: 16px;
  background-color: #fefefe;
  border: 1px solid ${colours.borderGrey};
  line-height: 22px;
  color: #000d;
  font-size: 12px;
  transition: all 0.2s;
  overflow: hidden;

  &:hover {
    border-color: ${colours.linkBlue};
    color: ${colours.linkBlue};
  }
`;

const pillButtonReset = `
  border: none;
  background: transparent;
  cursor: pointer;
  color: inherit;
  font-size: inherit;
  line-height: inherit;
  padding: 0;
  margin: 0;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const IconPinSlot = styled.button`
  ${pillButtonReset}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 3px 2px 3px 10px;
`;

const SelectText = styled.button`
  ${pillButtonReset}
  display: inline-flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  white-space: normal;
  word-break: break-word;
  text-align: left;
  padding: 3px 6px 3px 4px;
`;

const PillDelete = styled.button`
  ${pillButtonReset}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 3px 8px 3px 4px;
  opacity: 0.55;
  transition: opacity 0.15s, color 0.15s;

  &:hover:not(:disabled) {
    opacity: 1;
    color: ${colours.red};
  }

  &:disabled {
    opacity: 0.35;
  }
`;

const PIN_LIMIT_TOOLTIP = 'Only 5 items can be pinned at a time.';

export const RecentSearchItem = ({
  item,
  isPinned,
  disabled,
  pinDisabled,
  getIcon,
  onSelect,
  onPin,
  onDelete,
}) => {
  const [hovered, setHovered] = useState(false);
  const [pinFocused, setPinFocused] = useState(false);

  const handlePinClick = useCallback(
    e => {
      e.stopPropagation();
      onPin?.(item);
    },
    [item, onPin]
  );

  const handleDeleteClick = useCallback(
    e => {
      e.stopPropagation();
      onDelete?.(item);
    },
    [item, onDelete]
  );

  const handleSelect = useCallback(() => {
    if (!disabled) onSelect?.(item);
  }, [item, disabled, onSelect]);

  const defaultIcon = getIcon ? getIcon(item) : null;
  const canPin = Boolean(onPin);
  const showDelete = Boolean(onDelete);
  const hasLeadingSlot = canPin || Boolean(defaultIcon);
  const pinLimitBlocks = Boolean(pinDisabled && !isPinned);
  const pinButtonDisabled = Boolean(disabled || pinLimitBlocks);
  const showPinIcon = hovered || pinFocused || !defaultIcon;
  const pinLimitTooltip = pinLimitBlocks && !disabled;

  const pinTitle = disabled
    ? ''
    : pinLimitBlocks
    ? PIN_LIMIT_TOOLTIP
    : isPinned
    ? 'Unpin'
    : 'Pin';

  const pinAriaLabel = pinLimitBlocks
    ? PIN_LIMIT_TOOLTIP
    : isPinned
    ? 'Unpin'
    : 'Pin';

  // One element whether it shows the pin or the view icon, so keyboard focus
  // survives the icon swap on hover.
  const pinButton = (
    <IconPinSlot
      data-testid={`nlp-recent-search-pin-${item.id}`}
      type='button'
      onClick={handlePinClick}
      onFocus={() => setPinFocused(true)}
      onBlur={() => setPinFocused(false)}
      disabled={pinButtonDisabled}
      style={pinLimitTooltip ? { pointerEvents: 'none' } : undefined}
      title={pinLimitTooltip ? '' : pinTitle}
      aria-label={pinAriaLabel}
    >
      <Icon
        icon={showPinIcon ? 'pin' : defaultIcon}
        size='xs'
        color={
          showPinIcon && isPinned
            ? colours.linkBlue
            : colours.character.primary85
        }
      />
    </IconPinSlot>
  );

  return (
    <ItemWrapper
      data-testid={`nlp-recent-search-${item.id}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <PillShell>
        {canPin ? (
          pinLimitTooltip ? (
            <span
              title={pinTitle}
              style={{ display: 'inline-flex', alignItems: 'stretch' }}
            >
              {pinButton}
            </span>
          ) : (
            pinButton
          )
        ) : defaultIcon ? (
          <IconPinSlot as='span' aria-hidden onClick={handleSelect}>
            <Icon
              icon={defaultIcon}
              size='xs'
              color={colours.character.primary85}
            />
          </IconPinSlot>
        ) : null}
        <SelectText
          data-testid={`nlp-recent-search-select-${item.id}`}
          type='button'
          onClick={handleSelect}
          disabled={disabled}
          style={{ paddingLeft: hasLeadingSlot ? 4 : 10 }}
        >
          {item.prompt}
        </SelectText>
        {showDelete && (
          <PillDelete
            data-testid={`nlp-recent-search-delete-${item.id}`}
            type='button'
            onClick={handleDeleteClick}
            disabled={disabled}
            title='Remove'
            aria-label='Remove from recent searches'
          >
            <Icon icon='close' size='xs' color={colours.character.primary85} />
          </PillDelete>
        )}
      </PillShell>
    </ItemWrapper>
  );
};
